import React, { useEffect, useState, useCallback } from 'react';
import { Document, Download } from 'react-iconly';
import {
  getPayments, getInvoices, getMilestones, startCheckout, invoiceUrl, formatIDR,
} from '../services/portalService';

const StatusPill = ({ status }) => (
  <span className={`pill pill-${status}`}>{status}</span>
);

const BillingPanel = ({ order, onChanged }) => {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, inv, ms] = await Promise.all([
        getPayments(order.id), getInvoices(order.id), getMilestones(order.id),
      ]);
      setPayments(p); setInvoices(inv); setMilestones(ms);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [order.id]);

  useEffect(() => { load(); }, [load]);

  const pay = async ({ plan, milestoneId, label }) => {
    setPaying(label); setError('');
    try {
      const { paymentUrl } = await startCheckout({ orderId: order.id, plan, milestoneId });
      if (paymentUrl) { window.location.href = paymentUrl; return; }
      throw new Error('No payment URL returned');
    } catch (e) {
      setError(e.message || 'Payment could not be started');
      setPaying(null);
    }
  };

  const total = Number(order.total_amount) || 0;
  const paid = Number(order.amount_paid) || 0;
  const remaining = Math.max(0, total - paid);

  const renderActions = () => {
    if (remaining <= 0) {
      return <p className="portal-muted">✓ This project is fully paid. Thank you!</p>;
    }
    if (order.payment_plan === 'full') {
      return (
        <button className="portal-btn portal-btn-primary portal-btn-block" disabled={!!paying}
          onClick={() => pay({ plan: 'full', label: 'full' })}>
          {paying === 'full' ? 'Redirecting…' : `Pay in full — ${formatIDR(remaining)}`}
        </button>
      );
    }
    if (order.payment_plan === 'dp50') {
      if (paid <= 0) {
        return (
          <button className="portal-btn portal-btn-primary portal-btn-block" disabled={!!paying}
            onClick={() => pay({ plan: 'dp50', label: 'dp50' })}>
            {paying === 'dp50' ? 'Redirecting…' : `Pay 50% down payment — ${formatIDR(total * 0.5)}`}
          </button>
        );
      }
      return (
        <button className="portal-btn portal-btn-primary portal-btn-block" disabled={!!paying}
          onClick={() => pay({ plan: 'remainder', label: 'remainder' })}>
          {paying === 'remainder' ? 'Redirecting…' : `Pay remaining balance — ${formatIDR(remaining)}`}
        </button>
      );
    }
    // milestone plan
    if (!milestones.length) {
      return <p className="portal-muted">No milestones yet. Open the <strong>Manager</strong> tab to generate a plan, then pay each milestone here.</p>;
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {milestones.map((m) => (
          <div key={m.id} className="portal-row" style={{ borderBottom: '1px solid var(--v2-color-lightgray)' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{m.sequence}. {m.title}</div>
              <div className="portal-muted">{formatIDR(m.amount)} · <StatusPill status={m.status} /></div>
            </div>
            {['paid', 'done'].includes(m.status)
              ? <span className="pill pill-paid">paid</span>
              : <button className="portal-btn portal-btn-dark" disabled={!!paying}
                  onClick={() => pay({ plan: 'milestone', milestoneId: m.id, label: m.id })}>
                  {paying === m.id ? '…' : 'Pay'}
                </button>}
          </div>
        ))}
      </div>
    );
  };

  if (loading) return <div className="portal-spinner" />;

  return (
    <div>
      {/* Payment summary */}
      <div className="portal-card" style={{ marginBottom: 20 }}>
        <div className="portal-row">
          <span className="portal-label">Project total</span>
          <span className="portal-amount">{formatIDR(total)}</span>
        </div>
        <div className="portal-row">
          <span className="portal-label">Paid so far</span>
          <span className="portal-amount" style={{ color: 'var(--v2-color-orange)' }}>{formatIDR(paid)}</span>
        </div>
        <div className="portal-row">
          <span className="portal-label">Remaining</span>
          <span className="portal-amount">{formatIDR(remaining)}</span>
        </div>
        <div className="portal-progress" style={{ marginTop: 14 }}>
          <span style={{ width: `${total ? Math.min(100, (paid / total) * 100) : 0}%` }} />
        </div>
        <div style={{ marginTop: 18 }}>{renderActions()}</div>
        {error && <p style={{ color: 'var(--v2-color-error)', fontSize: 12, marginTop: 12 }}>{error}</p>}
      </div>

      {/* Transactions */}
      <p className="portal-label" style={{ marginBottom: 10 }}>Transactions</p>
      {payments.length === 0 ? (
        <p className="portal-muted">No transactions yet.</p>
      ) : (
        <div className="portal-card">
          {payments.map((p) => (
            <div key={p.id} className="portal-row">
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{formatIDR(p.amount)} <span className="portal-muted">· {p.kind}</span></div>
                <div className="portal-muted">{p.doku_order_id} · {new Date(p.created_at).toLocaleDateString('id-ID')}</div>
              </div>
              <StatusPill status={p.status} />
            </div>
          ))}
        </div>
      )}

      {/* Invoices & receipts */}
      <p className="portal-label" style={{ margin: '24px 0 10px' }}>Invoices &amp; receipts</p>
      {invoices.length === 0 ? (
        <p className="portal-muted">Invoices appear here once a payment is started.</p>
      ) : (
        <div className="portal-card">
          {invoices.map((inv) => (
            <div key={inv.id} className="portal-row">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {inv.type === 'receipt' ? <Download set="bold" size={18} /> : <Document set="bold" size={18} />}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, textTransform: 'capitalize' }}>{inv.type} · {formatIDR(inv.amount)}</div>
                  <div className="portal-muted">{inv.invoice_number}</div>
                </div>
              </div>
              <a className="portal-btn portal-btn-ghost" href={invoiceUrl(inv.id)} target="_blank" rel="noreferrer">View</a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BillingPanel;
